import { useEffect, useState, ChangeEventHandler } from 'react';
import { useRecoilState } from 'recoil';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    CardContent,
    Container,
    Fade,
    IconButton,
    InputAdornment,
    Zoom,
} from '@mui/material';
import {
    Email,
    Lock,
    Login as LoginIcon,
    Visibility,
    VisibilityOff,
} from '@mui/icons-material';
import { useAxiosWrapper } from '../helpers/axios-wrapper';
import { initUser, User } from '../types.d';
import { errorState, userState } from '../state/state';

// Import Tempco UI components
import {
    TempcoButton,
    TempcoCard,
    TempcoTextField,
    TempcoHeading,
    TempcoBodyText,
    TempcoAlert,
    TempcoGradientText,
    tempcoColors,
} from '../components/TempcoUI';

export const Login = () => {
    const navigate = useNavigate();
    const axiosWrapper = useAxiosWrapper();
    
    const [user, setUserState] = useRecoilState(userState);
    const [error, setError] = useRecoilState(errorState);

    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const storedUser = sessionStorage.getItem('user');
        if (storedUser && storedUser !== 'null') {
            try {
                const parsedUser = JSON.parse(storedUser);
                if (parsedUser?.isLoggedIn && parsedUser?.token) {
                    navigate('/home');
                    return;
                }
            } catch (error) {
                console.warn('Failed to parse stored user data');
            }
        }

        setUserState(initUser());
        setError({ isError: false, status: 'n/a', message: 'n/a', requestSent: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        setUserState((prev: User) => ({
            ...prev,
            [e.target.id]: e.target.value,
        }));
    };

    const handleLogin = async () => {
        setLoading(true);

        try {
            const data = await axiosWrapper.login(user);
            const token: string = (data as any)?.[0]?.Token ?? (data as any)?.Token ?? '';

            if (token) {
                // Normalize custNo to always be an array
                let custNoArray: string[] = [];
                if (typeof data.custNo === 'string') {
                    // If it's a string, split by comma and trim whitespace
                    custNoArray = data.custNo.split(',').map(n => n.trim()).filter(n => n !== '');
                } else if (Array.isArray(data.custNo)) {
                    custNoArray = data.custNo;
                }

                const userCopy = {
                    ...user,
                    isLoggedIn: true,
                    token: token,
                    isAdmin: data.isAdmin,
                    custNo: custNoArray,
                };
                setUserState(userCopy);
                sessionStorage.setItem('user', JSON.stringify(userCopy));
                navigate('/home');
            } else {
                const userCopy = { ...user, isLoggedIn: false };
                setUserState(userCopy);
                sessionStorage.setItem('user', JSON.stringify(userCopy));
            }
        } catch (err) {
            console.error('Login error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleTogglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleLogin();
        }
    };
    
    const autofillStyles = {
        '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 1000px rgba(237, 28, 36, 0.05) inset',
            WebkitTextFillColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '8px',
            transition: 'background-color 5000s ease-in-out 0s',
        },
        '& input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
            WebkitBoxShadow: '0 0 0 1000px rgba(237, 28, 36, 0.05) inset',
            WebkitTextFillColor: 'rgba(255, 255, 255, 0.9)',
        },
    };

    return (
        <Box
            sx={{
                flex: 1,
                background: 'transparent', // Let the main gradient show through
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 2,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `
                        radial-gradient(circle at 20% 50%, rgba(237, 28, 36, 0.15) 0%, transparent 50%), 
                        radial-gradient(circle at 80% 20%, rgba(240, 82, 89, 0.15) 0%, transparent 50%),
                        radial-gradient(circle at 60% 80%, rgba(173, 18, 25, 0.1) 0%, transparent 50%)
                    `,
                    zIndex: 0,
                }
            }}
        >
            <Container maxWidth="sm" sx={{zIndex: 1}}>
                <Zoom in={true} timeout={800}>
                    <TempcoCard sx={{ overflow: 'hidden', boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)' }}>
                        {/* Header Section with Tempco Logo and Branding */}
                        <Box
                            sx={{
                                background: '#000000',
                                padding: 4,
                                textAlign: 'center',
                                position: 'relative',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'rgba(0, 0, 0, 0.1)',
                                    zIndex: 0,
                                },
                                '& > *': {
                                    position: 'relative',
                                    zIndex: 1,
                                }
                            }}
                        >
                            <Box
                                component="img"
                                src={process.env.PUBLIC_URL + '/Tempco.png'}
                                alt='Tempco Electric Heater Corporation'
                                sx={{
                                    height: 90,
                                    maxWidth: '100%',
                                    objectFit: 'contain',
                                    marginBottom: 2,
                                    filter: 'brightness(1.2) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
                                }}
                            />
                            
                            <TempcoHeading
                                variant="h3"
                                sx={{
                                    color: tempcoColors.white,
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                                    letterSpacing: '0.5px',
                                    marginBottom: 1,
                                }}
                            >
                                Order Tracker Web
                            </TempcoHeading>
                        </Box>

                        {/* Enhanced Login Form with Tempco Components */}
                        <CardContent sx={{padding: 4, backgroundColor: 'rgba(51, 51, 51, 0.98)'}}>
                            <Box sx={{ textAlign: 'center', marginBottom: 3 }}>
                                <TempcoGradientText variant="h5" component="h2">
                                    Welcome Back
                                </TempcoGradientText>
                                <TempcoBodyText variant="body2" sx={{ marginTop: 1, opacity: 0.8 }}>
                                    Please sign in to access your account
                                </TempcoBodyText>
                            </Box>

                            <Box component="form" onKeyPress={handleKeyPress}>
                                <TempcoTextField
                                    fullWidth
                                    id="username"
                                    label="Email Address"
                                    type="email"
                                    value={user.username || ''}
                                    onChange={handleChange}
                                    placeholder="name@tempco.com"
                                    variant="outlined"
                                    margin="normal"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Email sx={{color: 'rgba(255, 255, 255, 0.4)'}}/>
                                            </InputAdornment>
                                        ),
                                        sx: autofillStyles,
                                    }}
                                />

                                <TempcoTextField
                                    fullWidth
                                    id="password"
                                    label="Password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={user.password || ''}
                                    onChange={handleChange}
                                    variant="outlined"
                                    margin="normal"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock sx={{color: 'rgba(255, 255, 255, 0.4)'}}/>
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={handleTogglePasswordVisibility}
                                                    edge="end"
                                                    sx={{
                                                        color: 'rgba(255, 255, 255, 0.4)',
                                                        '&:hover': {
                                                            color: tempcoColors.primary,
                                                        }
                                                    }}
                                                >
                                                    {showPassword ? <VisibilityOff/> : <Visibility/>}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        sx: autofillStyles,
                                    }}
                                />

                                {/* Enhanced Tempco Login Button */}
                                <TempcoButton
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    onClick={handleLogin}
                                    disabled={loading}
                                    startIcon={<LoginIcon/>}
                                    sx={{
                                        marginTop: 3,
                                        marginBottom: 2,
                                        height: 56,
                                        fontSize: '1.1rem',
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        boxShadow: `0 6px 20px rgba(237, 28, 36, 0.4)`,
                                        '&:hover': {
                                            boxShadow: `0 8px 25px rgba(237, 28, 36, 0.6)`,
                                        },
                                        '&:disabled': {
                                            background: 'rgba(51, 51, 51, 0.5)',
                                            color: 'rgba(255, 255, 255, 0.3)',
                                            transform: 'none',
                                            boxShadow: 'none',
                                        },
                                    }}
                                >
                                    {loading ? 'Signing In...' : 'Sign In'}
                                </TempcoButton>
                            </Box>

                            {/* Enhanced Error Alert with Tempco Styling */}
                            <Fade in={error.isError}>
                                <Box sx={{marginTop: 2}}>
                                    <TempcoAlert severity="error">
                                        <strong>Access Denied:</strong> {error.message}
                                    </TempcoAlert>
                                </Box>
                            </Fade>
                        </CardContent>
                    </TempcoCard>
                </Zoom>
            </Container>
        </Box>
    );
};